import React, { useState, useEffect } from 'react';
import api from '../services/api';
/**
 * Self-contained sectie voor inhuur-bestellingen bij een werkorder.
 *
 * Twee modi:
 *  - LIVE   : prop `werkorderId` heeft een id  -> CRUD direct via API
 *  - DRAFT  : prop `werkorderId` is null + `onPendingChange` callback gegeven
 *             -> regels in lokale state, parent post ze na werkorder-create
 */
export default function InhuurNodig({ werkorderId, onPendingChange }) {
  const isDraft = !werkorderId;
  const [bestellingen, setBestellingen] = useState([]);
  const [resources, setResources] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    api.get('/inhuur/check').then(r => setEnabled(r.data.enabled !== false)).catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (enabled !== true) return;
    load();
    // eslint-disable-next-line
  }, [werkorderId, enabled]);

  async function load() {
    setLoading(true);
    try {
      const resourcesRes = await api.get('/inhuur/resources');
      setResources(resourcesRes.data || []);
      if (!isDraft) {
        const bestRes = await api.get('/inhuur/bestellingen?werkorder_id=' + werkorderId);
        setBestellingen(bestRes.data || []);
      }
    } catch (e) {
      console.warn('Inhuur load fout:', e.message);
    } finally {
      setLoading(false);
    }
  }

  const notify = (arr) => { if (onPendingChange) onPendingChange(arr); };

  function newRow() {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ resource_id: '', specificatie: '', datum_van: today, datum_tot: today, aantal: 1, opmerking: '' });
    setShowForm(true);
  }

  async function save() {
    if (!form.resource_id) { alert('Kies een resource'); return; }
    try {
      if (isDraft) {
        const selRes = resources.find(r => String(r.id) === String(form.resource_id));
        const newOne = {
          id: 'draft-' + Date.now(),
          resource_id: parseInt(form.resource_id),
          resource_naam: selRes ? selRes.naam : '',
          resource_kleur: selRes ? selRes.kleur : '#f59e0b',
          resource_leverancier: selRes ? selRes.leverancier : null,
          specificatie: form.specificatie || null,
          datum_van: form.datum_van,
          datum_tot: form.datum_tot,
          aantal: parseInt(form.aantal) || 1,
          opmerking: form.opmerking || null,
          status: 'concept'
        };
        const updated = [...bestellingen, newOne];
        setBestellingen(updated); notify(updated);
      } else {
        await api.post('/inhuur/bestellingen', {
          resource_id: parseInt(form.resource_id),
          specificatie: form.specificatie || null,
          datum_van: form.datum_van,
          datum_tot: form.datum_tot,
          aantal: parseInt(form.aantal) || 1,
          opmerking: form.opmerking || null,
          werkorder_id: werkorderId
        });
        await load();
      }
      setShowForm(false); setForm(null);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm('Inhuur-regel verwijderen?')) return;
    try {
      if (isDraft) {
        const updated = bestellingen.filter(b => b.id !== id);
        setBestellingen(updated); notify(updated);
      } else {
        await api.delete('/inhuur/bestellingen/' + id);
        await load();
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  }

  if (enabled !== true) return null;

  const STATUS_ICON = { aangevraagd:'🤝', bevestigd:'✅', geleverd:'📦', geannuleerd:'❌', afgerekend:'💰', concept:'⏳' };
  const selRes = form ? resources.find(r => String(r.id) === String(form.resource_id)) : null;
  const specOpties = (selRes && selRes.specificatie_opties) ? selRes.specificatie_opties.split(',').map(s => s.trim()).filter(Boolean) : [];

  return (
    <div style={{ marginTop: 16, padding: 16, background: '#fff7ed', borderRadius: 8, border: '1px dashed #f59e0b' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: '#92400e', fontSize: 16 }}>
          🤝 Inhuur nodig ({bestellingen.length})
          {isDraft && <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, color: '#92400e' }}>(concept — wordt gekoppeld bij Werkorder Aanmaken)</span>}
        </h3>
        <button type="button" onClick={newRow} style={{ padding: '6px 12px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>+ Toevoegen</button>
      </div>

      {loading ? <div style={{ color: '#92400e', fontSize: 13 }}>Laden…</div> : (
        bestellingen.length === 0 ? (
          <div style={{ color: '#92400e', fontSize: 13, fontStyle: 'italic' }}>Nog geen inhuur gekoppeld.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fed7aa' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>Resource</th>
                <th style={{ padding: 6, textAlign: 'center', width: 60 }}>Aantal</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Datum</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Status</th>
                <th style={{ padding: 6, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {bestellingen.map(b => (
                <tr key={b.id} style={{ borderBottom: '1px solid #fed7aa' }}>
                  <td style={{ padding: 6 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, background: b.resource_kleur, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' }}/>
                    {b.resource_naam}
                    {b.resource_leverancier && <span style={{ color: '#92400e', fontSize: 11 }}> ({b.resource_leverancier})</span>}
                    {b.specificatie && <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginLeft: 14 }}>↳ {b.specificatie}</div>}
                  </td>
                  <td style={{ padding: 6, textAlign: 'center' }}>{b.aantal}</td>
                  <td style={{ padding: 6 }}>
                    {new Date(b.datum_van).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                    {b.datum_van !== b.datum_tot && ' t/m ' + new Date(b.datum_tot).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                  </td>
                  <td style={{ padding: 6 }}>{STATUS_ICON[b.status] || ''} {b.status}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    <button type="button" onClick={() => remove(b.id)} style={{ background: 'none', border: '1px solid #fecaca', color: '#ef4444', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {showForm && form && (
        <div style={{ marginTop: 12, padding: 12, background: '#fff', border: '1px solid #fed7aa', borderRadius: 6 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={form.resource_id} onChange={e => setForm({ ...form, resource_id: e.target.value, specificatie: '' })} style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}>
              <option value="">— kies resource —</option>
              {resources.map(r => <option key={r.id} value={r.id}>{r.naam}{r.leverancier ? ' (' + r.leverancier + ')' : ''}</option>)}
            </select>
            <input type="number" min="1" value={form.aantal} onChange={e => setForm({ ...form, aantal: e.target.value })} style={{ width: 70, padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}/>
          </div>
          {selRes && selRes.specificatie_label && (
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#92400e', display: 'block', marginBottom: 2 }}>{selRes.specificatie_label}</label>
              {specOpties.length > 0 ? (
                <select value={form.specificatie} onChange={e => setForm({ ...form, specificatie: e.target.value })} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db', boxSizing: 'border-box' }}>
                  <option value="">— kies —</option>
                  {specOpties.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={form.specificatie} onChange={e => setForm({ ...form, specificatie: e.target.value })} placeholder={'Bv. ' + selRes.specificatie_label} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db', boxSizing: 'border-box' }}/>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="date" value={form.datum_van} onChange={e => setForm({ ...form, datum_van: e.target.value })} style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}/>
            <input type="date" value={form.datum_tot} onChange={e => setForm({ ...form, datum_tot: e.target.value })} style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}/>
          </div>
          <input value={form.opmerking} onChange={e => setForm({ ...form, opmerking: e.target.value })} placeholder="Opmerking (optioneel)" style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db', marginBottom: 8, boxSizing: 'border-box' }}/>
          <div style={{ textAlign: 'right' }}>
            <button type="button" onClick={() => { setShowForm(false); setForm(null); }} style={{ padding: '6px 12px', background: '#e5e7eb', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 6 }}>Annuleren</button>
            <button type="button" onClick={save} style={{ padding: '6px 12px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Opslaan</button>
          </div>
        </div>
      )}
    </div>
  );
}
